import { useEffect, useState, useCallback } from "react";
import { AlertCircle, Download } from "lucide-react";
import {
  fetchReturnsAudit,
  type ReturnMetricsAudit,
} from "../../api/client";
import { fmtPercent, fmtMultiple } from "../../utils/format";
import { downloadCSV } from "../../utils/csv";
import LoadingSpinner from "../LoadingSpinner";
import {
  ExitPriceBuildup,
  IRRCashFlowStream,
  AnnualReturnMetrics,
  InvestmentSummary,
} from "./ReturnsCards";

// ---------- types ----------

interface ReturnsTabProps {
  dealId: string;
  scenarioId: string;
}

// ---------- Main component ----------

export default function ReturnsTab({ dealId, scenarioId }: ReturnsTabProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReturnMetricsAudit | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchReturnsAudit(dealId, scenarioId);
      setData(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load returns audit");
    } finally {
      setLoading(false);
    }
  }, [dealId, scenarioId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white border border-grove-300 rounded-xl p-8 text-center">
        <p className="text-sm text-bark-400">No returns audit data available.</p>
      </div>
    );
  }

  function exportReturnsCSV() {
    if (!data) return;
    const rows: (string | number)[][] = [];

    // Investment Summary
    rows.push(["--- Investment Summary ---"]);
    rows.push(["Purchase Price", data.purchase_price]);
    rows.push(["Loan Amount", data.loan_amount]);
    rows.push(["Equity", data.equity]);
    rows.push(["Going-In Cap Rate", data.going_in_cap != null ? fmtPercent(data.going_in_cap, 2) : ""]);
    rows.push([]);

    // Exit Price Buildup
    rows.push(["--- Exit Price Buildup ---"]);
    rows.push(["Final Year EGI", data.final_year_egi]);
    rows.push(["Year N+1 EGI", data.year_n_plus_1_egi]);
    rows.push(["Final Year OpEx", data.final_year_opex]);
    rows.push(["Year N+1 OpEx", data.year_n_plus_1_opex]);
    rows.push(["Year N+1 NOI", data.year_n_plus_1_noi]);
    rows.push(["Exit Cap Rate", fmtPercent(data.exit_cap_rate, 2)]);
    rows.push(["Gross Exit Price", data.gross_exit_price]);
    rows.push(["Selling Costs", data.selling_costs]);
    rows.push(["Net Exit Proceeds", data.net_exit_proceeds]);
    rows.push([]);

    // IRR Cash Flow Stream
    rows.push(["--- IRR Cash Flow Stream ---"]);
    rows.push(["Year", "Unlevered", "Levered"]);
    const maxLen = Math.max(data.unlevered_cf_stream.length, data.levered_cf_stream.length);
    for (let i = 0; i < maxLen; i++) {
      rows.push([
        i === 0 ? "Year 0 (Investment)" : i === maxLen - 1 ? `Year ${i} (+ Exit)` : `Year ${i}`,
        data.unlevered_cf_stream[i] ?? "",
        data.levered_cf_stream[i] ?? "",
      ]);
    }
    rows.push([]);

    // Return Metrics
    rows.push(["--- Return Metrics ---"]);
    rows.push(["Unlevered IRR", data.irr_unlevered != null ? fmtPercent(data.irr_unlevered, 2) : ""]);
    rows.push(["Levered IRR", data.irr_levered != null ? fmtPercent(data.irr_levered, 2) : ""]);
    rows.push(["NPV", data.npv != null ? Math.round(data.npv) : ""]);
    rows.push(["Equity Multiple", data.equity_multiple != null ? fmtMultiple(data.equity_multiple) : ""]);
    rows.push([]);

    // Annual Metrics
    rows.push(["--- Annual Return Metrics ---"]);
    rows.push(["Year", "Cash-on-Cash %", "DSCR"]);
    for (let i = 0; i < data.annual_dscr.length; i++) {
      rows.push([
        `Year ${i + 1}`,
        data.annual_coc[i] != null ? fmtPercent(data.annual_coc[i], 1) : "",
        data.annual_dscr[i] != null ? fmtMultiple(data.annual_dscr[i]) : "",
      ]);
    }

    downloadCSV(rows, "audit_returns.csv");
  }

  return (
    <div className="space-y-6">
      {/* Download button */}
      <div className="flex justify-end print:hidden">
        <button
          onClick={exportReturnsCSV}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
        >
          <Download className="w-4 h-4" />
          CSV
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ExitPriceBuildup data={data} />
        <InvestmentSummary data={data} />
      </div>
      <IRRCashFlowStream data={data} />
      <AnnualReturnMetrics data={data} />
    </div>
  );
}
